import type { ComponentPropsWithoutRef, ElementRef } from "react";
import { forwardRef } from "react";
import { tv } from "tailwind-variants";

const pricingCardVariants = tv({
  slots: {
    root: "flex flex-col overflow-hidden rounded-lg border border-transparent bg-white text-neutral-800",
    thumbnail: "size-full h-60 bg-white object-cover",
    container: "relative flex w-full flex-col items-start gap-2 p-6",
    badgeContainer: "absolute -top-8 left-0 flex w-full justify-center",
    subtitle: "-mb-1 font-semibold text-primary-900 md:h-5",
    title: "text-left text-2xl font-semibold text-primary-950",
    description: "text-left text-sm text-neutral-800 md:min-h-5",
    previousPrice: "pr-2 text-3xl font-semibold text-neutral-600 line-through",
    price: "text-5xl text-neutral-950",
    priceDetails: "text-sm",
    disclaimer: "text-start text-sm text-neutral-950",
    list: "list-inside list-disc pl-1.5 text-left text-sm [&_em]:text-neutral-600 [&_em]:not-italic",
    discountAlert:
      "discount-alert rounded-2xl bg-neutral-300 p-3 text-left text-sm [&>strong]:text-yellow-950",
    footer: "flex grow flex-col items-start gap-2",
  },
  variants: {
    hasDurationInMonths: {
      true: {
        priceDetails: "block",
      },
    },
    hasDiscount: {
      false: {
        disclaimer: "-translate-y-1",
      },
    },
  },
});

const {
  root,
  thumbnail,
  container,
  badgeContainer,
  subtitle,
  title,
  description,
  previousPrice,
  price,
  disclaimer,
  list,
  priceDetails,
  discountAlert,
  footer,
} = pricingCardVariants();

const Root = forwardRef<ElementRef<"div">, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => {
    return <div ref={ref} className={root({ className })} {...props} />;
  },
);
Root.displayName = "PricingCard.Root";

const Thumbnail = forwardRef<
  ElementRef<"img">,
  ComponentPropsWithoutRef<"img">
>(({ alt, className, ...props }, ref) => {
  return (
    <img
      ref={ref}
      alt={alt}
      className={thumbnail({ className })}
      draggable="false"
      {...props}
    />
  );
});
Thumbnail.displayName = "PricingCard.Thumbnail";

const Container = forwardRef<
  ElementRef<"div">,
  ComponentPropsWithoutRef<"div">
>(({ className, ...props }, ref) => {
  return <div ref={ref} className={container({ className })} {...props} />;
});
Container.displayName = "PricingCard.Container";

const BadgeContainer = forwardRef<
  ElementRef<"div">,
  ComponentPropsWithoutRef<"div">
>(({ className, ...props }, ref) => {
  return <div ref={ref} className={badgeContainer({ className })} {...props} />;
});
BadgeContainer.displayName = "PricingCard.BadgeContainer";

const Subtitle = forwardRef<ElementRef<"p">, ComponentPropsWithoutRef<"p">>(
  ({ className, ...props }, ref) => {
    return <p ref={ref} className={subtitle({ className })} {...props} />;
  },
);
Subtitle.displayName = "PricingCard.Subtitle";

const Title = forwardRef<ElementRef<"h4">, ComponentPropsWithoutRef<"h4">>(
  ({ className, children, ...props }, ref) => {
    return (
      <h4 ref={ref} className={title({ className })} {...props}>
        {children}
      </h4>
    );
  },
);
Title.displayName = "PricingCard.Title";

const Description = forwardRef<ElementRef<"p">, ComponentPropsWithoutRef<"p">>(
  ({ className, ...props }, ref) => {
    return <p ref={ref} className={description({ className })} {...props} />;
  },
);
Description.displayName = "PricingCard.Description";

const PreviousPrice = forwardRef<
  ElementRef<"span">,
  ComponentPropsWithoutRef<"span">
>(({ className, ...props }, ref) => {
  return <span ref={ref} className={previousPrice({ className })} {...props} />;
});
PreviousPrice.displayName = "PricingCard.PreviousPrice";

const Price = forwardRef<
  ElementRef<"strong">,
  ComponentPropsWithoutRef<"strong">
>(({ className, ...props }, ref) => {
  return <strong ref={ref} className={price({ className })} {...props} />;
});
Price.displayName = "PricingCard.Price";

const PriceDetails = forwardRef<
  ElementRef<"span">,
  ComponentPropsWithoutRef<"span"> & { hasDurationInMonths: boolean }
>(({ hasDurationInMonths, className, ...props }, ref) => {
  return (
    <span
      ref={ref}
      className={priceDetails({ hasDurationInMonths, className })}
      {...props}
    />
  );
});
PriceDetails.displayName = "PricingCard.PriceDetails";

const Disclaimer = forwardRef<
  ElementRef<"p">,
  ComponentPropsWithoutRef<"p"> & { hasDiscount: boolean }
>(({ hasDiscount, className, ...props }, ref) => {
  return (
    <p
      ref={ref}
      className={disclaimer({ hasDiscount, className })}
      {...props}
    />
  );
});
Disclaimer.displayName = "PricingCard.Disclaimer";

const DiscountAlert = forwardRef<
  ElementRef<"div">,
  ComponentPropsWithoutRef<"div">
>(({ className, ...props }, ref) => {
  return <div ref={ref} className={discountAlert({ className })} {...props} />;
});
DiscountAlert.displayName = "PricingCard.DiscountAlert";

const Footer = forwardRef<ElementRef<"div">, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => {
    return <div ref={ref} className={footer({ className })} {...props} />;
  },
);
Footer.displayName = "PricingCard.Footer";

const FeaturesList = forwardRef<
  ElementRef<"ul">,
  ComponentPropsWithoutRef<"ul">
>(({ className, ...props }, ref) => {
  return <ul ref={ref} className={list({ className })} {...props} />;
});
FeaturesList.displayName = "PricingCard.FeaturesList";

/**
 * @deprecated use `ShowcaseCard` instead
 */
export const PricingCard = {
  Root,
  Thumbnail,
  Container,
  BadgeContainer,
  Subtitle,
  Title,
  Description,
  PreviousPrice,
  Price,
  PriceDetails,
  Disclaimer,
  DiscountAlert,
  Footer,
  FeaturesList,
};
